import React, { createContext, useContext, useRef, ReactNode } from 'react'

interface ScrollState {
  scrollTop: number
  shareId: string | undefined
}

interface VersionsHistoryScrollPositionContextValue {
  scrollStateRef: React.MutableRefObject<ScrollState | undefined>
}

const VersionsHistoryScrollPositionContext = createContext<
  VersionsHistoryScrollPositionContextValue | undefined
>(undefined)

interface VersionsHistoryScrollPositionProviderProps {
  shareId?: string
  children: ReactNode
}

/**
 * Context to save the scroll position of the versions history list in memory.
 * We use it to restore the scroll position when navigating between versions in the version history list.
 * Ideally this would not be needed but because of our layout remounting everything when switching between versions,
 * we need to save the scroll position in memory and restore it when the component remounts.
 */
export function VersionsHistoryScrollPositionProvider({
  children,
}: VersionsHistoryScrollPositionProviderProps) {
  const scrollStateRef = useRef<ScrollState>()

  return (
    <VersionsHistoryScrollPositionContext.Provider value={{ scrollStateRef }}>
      {children}
    </VersionsHistoryScrollPositionContext.Provider>
  )
}

/**
 * Access the ref to save the scroll position of the versions history list
 * in memory.
 * @see VersionsHistoryScrollPositionProvider
 */
export function useVersionsHistorySavedScrollPosition() {
  const context = useContext(VersionsHistoryScrollPositionContext)

  if (!context) {
    throw new Error(
      'useVersionsHistoryScrollPosition must be used within VersionsHistoryScrollPositionProvider'
    )
  }

  return context.scrollStateRef
}
